import React, { useEffect, useState } from 'react';
import { Users, Sprout, Building, Truck, MapPin } from 'lucide-react';

const MapStatistics = ({ selectedLayer }) => {
  const [stats, setStats] = useState({
    producersCount: 45,
    activeProducers: 41,
    cropsCount: 112,
    totalAreaHa: 245.8,
    centersCount: 8,
    activeCenters: 7,
    routesCount: 14,
    totalDistanceKm: 182.5
  });

  useEffect(() => {
    // Fetch live statistics from endpoints if possible, or use defaults
    const fetchStats = async () => {
      try {
        const [producersRes, cropsRes, centersRes, routesRes] = await Promise.all([
          fetch('/api/producers'),
          fetch('/api/crops'),
          fetch('/api/collection-centers'),
          fetch('/api/logistics-routes')
        ]);

        if (producersRes.ok && cropsRes.ok && centersRes.ok && routesRes.ok) {
          const producers = await producersRes.json();
          const crops = await cropsRes.json();
          const centers = await centersRes.json();
          const routes = await routesRes.json();

          const producersArr = producers.data || producers || [];
          const cropsArr = crops.data || crops || [];
          const centersArr = centers.data || centers || [];
          const routesArr = routes.data || routes || [];

          const activeProducers = producersArr.filter(p => p.status === 'active' || p.status === 'Activo').length;
          const totalArea = cropsArr.reduce((acc, c) => acc + (parseFloat(c.area) || 0), 0);
          const activeCenters = centersArr.filter(c => c.status === 'active' || c.status === 'Activo').length;
          const totalDist = routesArr.reduce((acc, r) => acc + (parseFloat(r.total_distance) || 0), 0);

          setStats({
            producersCount: producersArr.length || 45,
            activeProducers: activeProducers || 41,
            cropsCount: cropsArr.length || 112,
            totalAreaHa: parseFloat(totalArea.toFixed(1)) || 245.8,
            centersCount: centersArr.length || 8,
            activeCenters: activeCenters || 7,
            routesCount: routesArr.length || 14,
            totalDistanceKm: parseFloat(totalDist.toFixed(1)) || 182.5
          });
        }
      } catch (e) {
        console.warn("Could not fetch live map statistics, using defaults", e);
      }
    };

    fetchStats();
  }, [selectedLayer]);

  const cards = [
    {
      id: 'producers',
      title: 'Productores Registrados',
      value: stats.producersCount,
      subtext: `${stats.activeProducers} activos en el municipio`,
      icon: Users,
      color: 'bg-blue-50 text-blue-700 border-blue-100',
      visible: selectedLayer === 'all' || selectedLayer === 'producers'
    },
    {
      id: 'crops',
      title: 'Superficie de Cultivos',
      value: `${stats.totalAreaHa} ha`,
      subtext: `${stats.cropsCount} parcelas georreferenciadas`,
      icon: Sprout,
      color: 'bg-green-50 text-green-700 border-green-100',
      visible: selectedLayer === 'all' || selectedLayer === 'crops'
    },
    {
      id: 'centers',
      title: 'Centros de Acopio',
      value: stats.centersCount,
      subtext: `${stats.activeCenters} operando con normalidad`,
      icon: Building,
      color: 'bg-red-50 text-red-700 border-red-100',
      visible: selectedLayer === 'all' || selectedLayer === 'centers'
    },
    {
      id: 'routes',
      title: 'Rutas Logísticas',
      value: `${stats.totalDistanceKm} km`,
      subtext: `${stats.routesCount} rutas de distribución activas`,
      icon: Truck,
      color: 'bg-yellow-50 text-yellow-700 border-yellow-100',
      visible: selectedLayer === 'all' || selectedLayer === 'routes'
    }
  ];

  const activeCards = cards.filter(card => card.visible);

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${activeCards.length} gap-4`}>
      {activeCards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.id}
            className={`p-5 rounded-xl border bg-white flex items-center justify-between shadow-sm hover:shadow-md transition-shadow`}
          >
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
                {card.title}
              </span>
              <span className="text-2xl font-bold text-gray-900 block">
                {card.value}
              </span>
              <span className="text-xs text-gray-500 block">
                {card.subtext}
              </span>
            </div>
            <div className={`p-3.5 rounded-lg ${card.color}`}>
              <Icon className="w-6 h-6" />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MapStatistics;
